import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Card } from '@/components/ui';
import { readUser } from '../../../api/user.api';
import type { User } from '../../../types/user.types';
import { useToast } from '../../../contexts/toast-context';
import { TOAST_TYPES } from '@/constants/common.constants';
import ROLES from '@/constants/roles.constants';
import {
  BaseLayout,
  SkeletonLoader,
  ExecutiveDashboard,
  ManagerDashboard,
  DriverDashboardView,
  StaffDashboard,
  UserProfile,
} from '@/components';
import { AuthError } from '../../../utils/errors';
import { useAuth } from '../../../hooks/use-auth';
import './view-user-style.scss';

export const Route = createFileRoute('/organizations/$uuid/')({
  component: UserSummary,
});

function UserSummary() {
  const { uuid } = Route.useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setIsLoading(true);
        const data = await readUser(uuid);
        setUser(data.user);
      } catch (error) {
        addToast(`Failed to fetch user profile`, {
          type: TOAST_TYPES.ERROR,
          title: 'Fetch Error',
        });
        if (!(error instanceof AuthError)) {
          navigate({ to: '/organizations' });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [uuid]);

  if (isLoading) {
    return (
      <BaseLayout
        header={{
          headerText: 'User Details',
        }}
        breadcrumbs={{
          showBreadcrumbs: true,
          breadcrumbText: ['Users', 'Summary'],
        }}
      >
        <SkeletonLoader count={10} height={30} />
      </BaseLayout>
    );
  }

  if (!user) {
    return null;
  }

  const canEditProfile = () => {
    if (!currentUser || !user) return false;
    if (currentUser.role === ROLES.ADMINISTRATOR) return true;
    if (currentUser.role === ROLES.EXECUTIVE) {
      return currentUser.account === user.accountName;
    }
    if (currentUser.role === ROLES.MANAGER) {
      return (
        (user.role === ROLES.DRIVER && user.managerUuid === currentUser.uuid) ||
        (user.role === ROLES.MANAGER && user.uuid === currentUser.uuid)
      );
    }
    return false;
  };

  // Format role for display
  const formatRole = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  // Render appropriate dashboard based on user role
  const renderDashboard = () => {
    switch (user.role) {
      case ROLES.EXECUTIVE:
        return (
          <ExecutiveDashboard
            accountUuid={user.accountUuid ?? undefined}
            userUuid={user.uuid}
            organizationLevelUuid={user.organizationLevelUuid ?? undefined}
            liveAlerts
          />
        );

      case ROLES.MANAGER:
        return (
          <ManagerDashboard
            accountUuid={user.accountUuid ?? undefined}
            userUuid={user.uuid}
            organizationLevelUuid={user.organizationLevelUuid ?? undefined}
            liveAlerts
          />
        );

      case ROLES.STAFF:
        return (
          <StaffDashboard
            accountUuid={user.accountUuid ?? undefined}
            userUuid={user.uuid}
            organizationLevelUuid={user.organizationLevelUuid ?? undefined}
            liveAlerts
          />
        );

      case ROLES.DRIVER:
        return (
          <DriverDashboardView
            accountUuid={user.accountUuid ?? undefined}
            userUuid={user.uuid}
            driverName={user.name}
          />
        );

      default:
        return null;
    }
  };

  // Build badges array
  const badges: Array<{
    text: string;
    styles?: {
      backgroundColor?: string;
      color?: string;
      borderColor?: string;
    };
  }> = [
    {
      text: formatRole(user.role),
    },
  ];

  // Add deactivated badge if user is deactivated
  if (user.status === 'deactivated') {
    badges.push({
      text: 'Deactivated',
      styles: {
        backgroundColor: 'var(--danger-light-1)',
        color: 'var(--danger-dark-1)',
      },
    });
  }

  return (
    <BaseLayout
      header={{
        headerText: user.name,
        subHeaderText: user.organizationLevel
          ? `${user.accountName} | ${user.organizationLevel}`
          : user?.accountName || undefined,
        headerBadges: badges,
        headerChildren: (
          <Button
            variant="basic-secondary"
            size="medium"
            onClick={() =>
              navigate({
                to: canEditProfile()
                  ? `/organizations/${uuid}/edit`
                  : `/organizations/${uuid}/profile`,
              })
            }
          >
            {canEditProfile() ? 'Edit Profile' : 'View Profile'}
          </Button>
        ),
      }}
      breadcrumbs={{
        showBreadcrumbs: true,
        breadcrumbText: [user.name],
      }}
    >
      {user.role === ROLES.ADMINISTRATOR ? (
        <Card>
          <UserProfile user={user} />
        </Card>
      ) : (
        renderDashboard()
      )}
    </BaseLayout>
  );
}
